import React, { useState, useRef, useEffect } from 'react';
import './ResizableSplitter.css';

interface ResizableSplitterProps {
  children: [React.ReactNode, React.ReactNode];
  initialSizes?: [number, number]; // percentages
  minSizes?: [number, number]; // percentages
  direction?: 'horizontal' | 'vertical';
  className?: string;
}

export function ResizableSplitter({
  children,
  initialSizes = [50, 50],
  minSizes = [20, 20],
  direction = 'horizontal',
  className = '',
}: ResizableSplitterProps) {
  // Load saved sizes from localStorage
  const savedSizes = localStorage.getItem('ectd-splitter-sizes');
  const defaultSizes = savedSizes ? JSON.parse(savedSizes) : initialSizes;
  const [sizes, setSizes] = useState<[number, number]>(defaultSizes);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startPosRef = useRef<number>(0);
  const startSizesRef = useRef<[number, number]>([0, 0]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startPosRef.current = direction === 'horizontal' ? e.clientX : e.clientY;
    startSizesRef.current = [...sizes];
  };

  const handleDoubleClick = () => {
    setSizes(initialSizes);
    localStorage.setItem('ectd-splitter-sizes', JSON.stringify(initialSizes));
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerSize = direction === 'horizontal' ? containerRect.width : containerRect.height;
      const currentPos = direction === 'horizontal' ? e.clientX : e.clientY;
      const delta = currentPos - startPosRef.current;
      const deltaPercent = (delta / containerSize) * 100;

      const newSize1 = Math.max(
        minSizes[0],
        Math.min(100 - minSizes[1], startSizesRef.current[0] + deltaPercent)
      );
      const newSize2 = 100 - newSize1;

      const newSizes: [number, number] = [newSize1, newSize2];
      setSizes(newSizes);
      
      // Save to localStorage
      localStorage.setItem('ectd-splitter-sizes', JSON.stringify(newSizes));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, direction, minSizes]);

  const splitterClass = `resizable-splitter ${direction} ${isDragging ? 'dragging' : ''}`;

  return (
    <div
      ref={containerRef}
      className={`resizable-container ${direction} ${className}`}
    >
      <div
        className="resizable-panel"
        style={{
          [direction === 'horizontal' ? 'width' : 'height']: `${sizes[0]}%`,
        }}
      >
        {children[0]}
      </div>
      
      <div
        className={splitterClass}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        title="Drag to resize, double-click to reset"
      >
        <div className="splitter-handle" />
      </div>
      
      <div
        className="resizable-panel"
        style={{
          [direction === 'horizontal' ? 'width' : 'height']: `${sizes[1]}%`,
        }}
      >
        {children[1]}
      </div>
    </div>
  );
}
